import React, { useState, useEffect, useRef } from 'react';
import { X, Sparkles, Loader2, Send, Edit3, Save } from 'lucide-react';
import { generateAiContentStream, generateOpsContent } from '../../api/ai';
import { User, Message } from '../../types';
import request from '../../api/request';

interface AskAIModalProps {
  isOpen?: boolean; // Make optional for compatibility or remove if always open when rendered
  onClose: () => void;
  onSend: (text: string) => void; // Changed from onInsert to onSend to match usage
  currentUser?: User | null; // Make optional
  chatHistory?: Message[]; // Make optional
}

const PROMPT_SUGGESTIONS = [
  '总结最近的对话',
  '建议一个友好的回复',
  '将最后一条消息翻译成英文',
  '检查我的草稿是否有语法错误',
  '润色我的回复',
  // '生成一份订单确认模板', // Removed as requested
];

const AskAIModal: React.FC<AskAIModalProps> = ({ onClose, onSend, currentUser, chatHistory }) => {
  const [prompt, setPrompt] = useState('');
  const [generatedText, setGeneratedText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Operations Agent Prompt State
  const [opsPrompt, setOpsPrompt] = useState('');
  const [isEditingOpsPrompt, setIsEditingOpsPrompt] = useState(false);
  const [loadingOpsPrompt, setLoadingOpsPrompt] = useState(false);

  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  // Fetch Operations Agent Prompt
   useEffect(() => {
     const fetchOpsPrompt = async () => {
       setLoadingOpsPrompt(true);
       try {
         const { data } = await request.get('/api/ai/ops/instructions');
         setOpsPrompt((data && data.instructions) || '');
       } catch (err) {
         console.error("Failed to fetch ops prompt", err);
       } finally {
         setLoadingOpsPrompt(false);
       }
     };
     fetchOpsPrompt();
   }, []);

  const handleSaveOpsPrompt = async () => {
    try {
        await request.put('/api/ai/ops/instructions', { instructions: opsPrompt });
        setIsEditingOpsPrompt(false);
    } catch (err) {
        console.error("Failed to save ops prompt", err);
        setError('保存提示词失败');
    }
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) return;
    setIsLoading(true);
    setError('');
    setGeneratedText('');

    try {
      // Use the new ops agent endpoint for manual tasks in this modal
      const result = await generateOpsContent(prompt);
      setGeneratedText(result);
      // 清空输入框，表示已经发送给AI
      setPrompt('');
    } catch (err) {
      setError('AI 生成失败，请稍后重试。');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = () => {
    if (generatedText) {
      onSend(generatedText);
      setPrompt('');  // 清空输入框
      setGeneratedText('');  // 清空生成的内容
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div
        ref={modalRef}
        className="bg-card border border-border rounded-lg shadow-2xl shadow-primary/20 w-full max-w-2xl flex flex-col animate-slide-in max-h-[90vh]"
      >
        <header className="flex items-center justify-between p-4 border-b border-border flex-shrink-0">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-accent" />
            AI 助手 (运营助理)
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X />
          </button>
        </header>
        <main className="p-6 space-y-4 overflow-y-auto flex-1">
          
          {/* Operations Agent Prompt Configuration Area */}
          <div className="bg-muted/20 p-3 rounded-md border border-border mb-4">
             <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> 
                    运营助理提示词配置
                </label>
                {!isEditingOpsPrompt ? (
                    <button 
                        onClick={() => setIsEditingOpsPrompt(true)}
                        className="text-xs text-primary hover:underline flex items-center gap-1"
                    >
                        <Edit3 className="w-3 h-3" /> 编辑
                    </button>
                ) : (
                    <button 
                        onClick={handleSaveOpsPrompt}
                        className="text-xs text-whatsapp-green hover:underline flex items-center gap-1"
                    >
                        <Save className="w-3 h-3" /> 保存
                    </button>
                )}
             </div>
             
             {isEditingOpsPrompt ? (
                 <textarea
                    value={opsPrompt}
                    onChange={(e) => setOpsPrompt(e.target.value)}
                    className="w-full p-2 bg-muted border border-border rounded text-xs font-mono focus:outline-none focus:ring-1 focus:ring-primary"
                    rows={3}
                 />
             ) : (
                 <div className="text-xs text-muted-foreground line-clamp-2 italic">
                    {loadingOpsPrompt ? '加载中...' : opsPrompt || '暂无提示词'}
                 </div>
             )}
          </div>

          <div>
            <label htmlFor="ai-prompt" className="text-sm font-medium text-muted-foreground block mb-2">你想让 AI 做什么？</label>
            <div className="flex gap-2">
                <textarea
                id="ai-prompt"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleGenerate();
                    }
                }}
                className="flex-1 p-3 bg-muted/50 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all resize-none"
                placeholder="输入指令，例如：回复用户关于价格的问题...（Shift+Enter 换行，Enter 发送）"
                rows={3}
                autoFocus
                />
                <button
                    onClick={handleGenerate}
                    disabled={isLoading || !prompt.trim()}
                    className="bg-primary text-primary-foreground px-4 rounded-md hover:bg-primary/90 disabled:opacity-50 transition-colors"
                >
                    {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                </button>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {PROMPT_SUGGESTIONS.map((suggestion, index) => (
              <button
                key={index}
                onClick={() => setPrompt(suggestion)}
                className="text-xs px-3 py-1.5 bg-muted hover:bg-muted/80 border border-border rounded-full transition-colors text-muted-foreground"
              >
                {suggestion}
              </button>
            ))}
          </div>

          {error && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 text-destructive rounded-md text-sm">
              {error}
            </div>
          )}

          {(generatedText || isLoading) && (
            <div className="mt-4 space-y-2">
              <label className="text-sm font-medium text-muted-foreground">AI 生成的内容:</label>
              <div className="p-4 bg-muted/30 border border-border rounded-md min-h-[100px] whitespace-pre-wrap text-sm leading-relaxed relative group">
                {isLoading && !generatedText ? (
                    <div className="flex items-center gap-2 text-muted-foreground">
                        <Sparkles className="w-4 h-4 animate-pulse text-accent" />
                        正在思考...
                    </div>
                ) : (
                    generatedText
                )}
              </div>
            </div>
          )}
        </main>
        <footer className="flex justify-end gap-3 p-4 border-t border-border flex-shrink-0">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium border border-border rounded-md hover:bg-muted transition-colors">
            取消
          </button>
          <button 
            onClick={handleSend} 
            disabled={!generatedText}
            className="px-4 py-2 text-sm font-medium bg-whatsapp-green text-white rounded-md hover:bg-whatsapp-green/90 disabled:opacity-50 transition-colors flex items-center gap-2"
          >
            <Send className="w-4 h-4" />
            使用此内容
          </button>
        </footer>
      </div>
    </div>
  );
};

export default AskAIModal;
